import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export const ChiftConfigurationVariablesSchema = z.object({
  tp_accounting_automatic_sync: z.boolean().optional(),
  tp_accounting_issue_date_limit: z.string().optional().nullable(),
  use_chift_import_integration: z.boolean().optional(),
  fetch_pdf: z.boolean().optional(),
  fetch_raw_data: z.boolean().optional(),
  unposted_allowed: z.boolean().optional(),
  daily_pdfs_sync: z.boolean().optional(),
});

export type ChiftConfigurationVariables = z.infer<typeof ChiftConfigurationVariablesSchema>;

export async function updateChiftConfiguration(
  axiosInstance: Promise<AxiosInstance>,
  data: ChiftConfigurationVariables,
) {
  const instance = await axiosInstance;

  return instance.put('/chift_configurations', data);
}

export function useUpdateChiftConfiguration() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, ChiftConfigurationVariables>({
    mutationFn: data => updateChiftConfiguration(axiosInstance, data),
    onSuccess: () => {
      client.invalidateQueries({
        queryKey: ['update-chift-configuration'],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    ChiftConfigurationVariables,
    'updateChiftConfiguration'
  >('updateChiftConfiguration', mutation);
}
